import { useEnterAnimation } from "@/pages/services/hooks/useEnterAnimation";

/**
 * PricingTransparency — direct, no-ambiguity fee list.
 *
 * No "contact for details" placeholders. Each row shows:
 *   · session type
 *   · duration
 *   · price
 *   · a one-line honest note about long-term pricing where relevant
 */

interface Row {
  index: string;
  label: string; // e.g. "Individual Therapy"
  duration: string; // "50 minutes"
  price: string; // "$120"
  note: string; // small honest note under the row
}

const ROWS: Row[] = [
  {
    index: "01",
    label: "Individual Therapy",
    duration: "50 minutes",
    price: "$120",
    note:
      "Book a 10-session package upfront and each session becomes $105. No obligation to continue after the first.",
  },
  {
    index: "02",
    label: "Couples Therapy",
    duration: "80 minutes",
    price: "$180",
    note:
      "Longer format because two voices need space. Same 10-session discount applies ($158 each).",
  },
  {
    index: "03",
    label: "Online Sessions",
    duration: "50 minutes",
    price: "$110",
    note:
      "Ten dollars less than in-person, because we save on room rental. Same therapist, same care.",
  },
  {
    index: "04",
    label: "First 20-minute call",
    duration: "20 minutes",
    price: "Free",
    note:
      "Before you commit to anything, we speak. No card, no booking pressure &mdash; just a conversation.",
  },
];

export default function PricingTransparency() {
  const eyebrowRef = useEnterAnimation<HTMLDivElement>({ delay: 0 });
  const titleRef = useEnterAnimation<HTMLHeadingElement>({ delay: 140 });
  const subtitleRef = useEnterAnimation<HTMLParagraphElement>({ delay: 300 });
  const row0Ref = useEnterAnimation<HTMLDivElement>({ delay: 460 });
  const row1Ref = useEnterAnimation<HTMLDivElement>({ delay: 580 });
  const row2Ref = useEnterAnimation<HTMLDivElement>({ delay: 700 });
  const row3Ref = useEnterAnimation<HTMLDivElement>({ delay: 820 });
  const rowRefs = [row0Ref, row1Ref, row2Ref, row3Ref];
  const noteRef = useEnterAnimation<HTMLDivElement>({ delay: 960 });

  return (
    <section
      id="pricing"
      className="relative w-full bg-background-50"
      style={{ minHeight: "100vh" }}
    >
      {/* Section meta strip */}
      <div className="absolute top-0 left-0 right-0 z-30 px-6 md:px-10 pt-14 md:pt-16 pointer-events-none">
        <div className="max-w-[1600px] mx-auto flex items-baseline justify-between">
          <span className="text-[10px] tracking-[0.35em] uppercase text-foreground-950/60 font-medium">
            Our Services / 05
          </span>
          <span className="text-[10px] tracking-[0.35em] uppercase text-foreground-950/60 font-medium hidden md:block">
            Transparent pricing
          </span>
        </div>
      </div>

      <div className="w-full min-h-screen flex items-center px-6 md:px-10 py-28 md:py-36">
        <div className="max-w-[1400px] w-full mx-auto">
          {/* Header */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 mb-20 md:mb-24">
            <div className="lg:col-span-6">
              <div ref={eyebrowRef} className="mb-8">
                <span className="inline-flex items-center gap-3 text-[10px] tracking-[0.35em] uppercase text-foreground-950/70 font-medium">
                  <span className="w-8 h-[1px] bg-foreground-950/35"></span>
                  Nothing hidden
                </span>
              </div>

              <h2
                ref={titleRef}
                style={{
                  fontFamily: "var(--font-heading)",
                  fontSize: "clamp(2.5rem, 5vw, 4.75rem)",
                  fontWeight: 300,
                  letterSpacing: "-0.025em",
                  lineHeight: 1.05,
                  color: "oklch(var(--foreground-950))",
                  fontVariationSettings: "'opsz' 144",
                }}
              >
                Fees, clearly.
              </h2>
            </div>

            <div className="lg:col-span-6 flex lg:items-end">
              <p
                ref={subtitleRef}
                className="italic max-w-[520px]"
                style={{
                  fontFamily: "var(--font-heading)",
                  fontSize: "clamp(1.05rem, 1.3vw, 1.3rem)",
                  fontWeight: 300,
                  lineHeight: 1.6,
                  letterSpacing: "0.005em",
                  color: "oklch(var(--foreground-950) / 0.72)",
                  fontVariationSettings: "'opsz' 144",
                }}
              >
                You&rsquo;ll never see &ldquo;inquire for details&rdquo; here.
                Below is exactly what a session costs, and what changes if you
                stay with us long-term.
              </p>
            </div>
          </div>

          {/* Rows */}
          <div className="border-t border-foreground-950/15">
            {ROWS.map((row, i) => (
              <div
                key={row.index}
                ref={rowRefs[i]}
                className="border-b border-foreground-950/15 py-8 md:py-10 grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10 items-baseline"
              >
                {/* Index + label */}
                <div className="md:col-span-5 flex items-baseline gap-4">
                  <span
                    style={{
                      fontFamily: "var(--font-heading)",
                      fontSize: "clamp(1rem, 1.1vw, 1.15rem)",
                      fontWeight: 400,
                      color: "oklch(var(--foreground-950) / 0.45)",
                      letterSpacing: "0.02em",
                    }}
                  >
                    {row.index}
                  </span>
                  <div
                    style={{
                      fontFamily: "var(--font-heading)",
                      fontSize: "clamp(1.5rem, 2.2vw, 2.15rem)",
                      fontWeight: 300,
                      letterSpacing: "-0.015em",
                      lineHeight: 1.15,
                      color: "oklch(var(--foreground-950))",
                      fontVariationSettings: "'opsz' 144",
                    }}
                  >
                    {row.label}
                  </div>
                </div>

                {/* Duration */}
                <div className="md:col-span-3">
                  <div className="text-[10px] tracking-[0.35em] uppercase text-foreground-950/55 font-medium mb-1.5">
                    Duration
                  </div>
                  <div
                    style={{
                      fontFamily: "var(--font-body)",
                      fontSize: "clamp(0.95rem, 1.05vw, 1.05rem)",
                      color: "oklch(var(--foreground-950) / 0.88)",
                    }}
                  >
                    {row.duration}
                  </div>
                </div>

                {/* Price */}
                <div className="md:col-span-2">
                  <div className="text-[10px] tracking-[0.35em] uppercase text-foreground-950/55 font-medium mb-1.5">
                    Per session
                  </div>
                  <div
                    style={{
                      fontFamily: "var(--font-heading)",
                      fontSize: "clamp(1.4rem, 2vw, 1.95rem)",
                      fontWeight: 400,
                      letterSpacing: "-0.01em",
                      color: "oklch(var(--foreground-950))",
                      fontVariationSettings: "'opsz' 144",
                    }}
                  >
                    {row.price}
                  </div>
                </div>

                {/* Honest note */}
                <div className="md:col-span-2">
                  <div
                    className="italic"
                    style={{
                      fontFamily: "var(--font-heading)",
                      fontSize: "clamp(0.85rem, 0.95vw, 0.95rem)",
                      fontWeight: 300,
                      lineHeight: 1.55,
                      color: "oklch(var(--foreground-950) / 0.7)",
                      letterSpacing: "0.005em",
                    }}
                    dangerouslySetInnerHTML={{ __html: row.note }}
                  />
                </div>
              </div>
            ))}
          </div>

          {/* Bottom footnote */}
          <div ref={noteRef} className="mt-10 md:mt-12 max-w-[720px]">
            <div className="text-[10px] tracking-[0.35em] uppercase text-foreground-950/55 font-medium mb-3">
              A note on money
            </div>
            <p
              style={{
                fontFamily: "var(--font-body)",
                fontSize: "clamp(0.95rem, 1.05vw, 1.05rem)",
                lineHeight: 1.7,
                color: "oklch(var(--foreground-950) / 0.82)",
              }}
            >
              If cost is what&rsquo;s keeping you away, please tell us. We hold
              a small number of sliding-scale slots each month for people
              between insurance, between jobs, or between chapters.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}